import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
} from '../../../components/CommandControlComponents'
import { Step, Warning } from '../../../components/DocsComponents'

const Resources = () => (
  <Page
    title="Resources"
    intro="Resources represent operational crews, vehicles, teams, officers, medical centres, and other deployable assets in a control room."
  >
    <Section title="Adding a resource">
      <Step number={1} title="Open the add-resource popup">
        Use the add button on the Resources module in Compact view.
      </Step>
      <Step number={2} title="Choose call sign or vehicle">
        Add a resource by typing a call sign, or switch to vehicle mode and
        pick a vehicle from your fleet list. Vehicle resources carry the
        vehicle's call sign and stay linked to its live location.
      </Step>
      <Step number={3} title="Resolve any conflict">
        If the call sign or vehicle is already live in another control room, a
        conflict view appears instead of adding a duplicate — see below.
      </Step>
      <Bullets>
        <Bullet>Call signs must be unique within a control room.</Bullet>
        <Bullet>Resource type can be set to Ambulance, Medical Centre, RRV, Response Team, Officer, or Other from the resource drawer.</Bullet>
        <Bullet>Resource notes appear as alerts in the Screen 2 resource list.</Bullet>
        <Bullet>Renaming a resource writes a system log entry.</Bullet>
      </Bullets>
      <Info>
        Who can do this? Adding, renaming, updating, and removing resources
        requires Dispatcher or Call Taker access (or above). Viewers see the
        resource board read-only.
      </Info>
    </Section>

    <Section title="Call signs, vehicles, and other control rooms">
      <Bullets>
        <Bullet>
          Call signs are scoped per room: two events can both run a
          &quot;ROMEO1&quot;, each with its own crew and status.
        </Bullet>
        <Bullet>
          Vehicles are the exception — a vehicle can only be live in one
          control room at a time.
        </Bullet>
        <Bullet>
          Adding a vehicle that is live elsewhere shows a conflict popup with
          the option to move it into your room. Moving strips the resource:
          crew are unassigned, location-sharing consents are cleared, any live
          tasking in the old room is cleared (its crew get a stand-down
          alert), and both rooms get a command log entry.
        </Bullet>
        <Bullet>
          Adding a call sign that is live elsewhere offers a choice: move that
          resource into your room, or add a separate resource with the same
          call sign.
        </Bullet>
        <Bullet>
          Vehicles deployed to the linked event are added to the control room
          automatically when it opens or reopens, with a system log entry for
          each — dispatchers do not need to re-key the fleet that event
          planners already allocated.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Status and break management">
      <Bullets>
        <Bullet>Resources can be marked Available, Off Duty, Break, Assigned, On Route, At Scene, On Scene, and other operational states.</Bullet>
        <Bullet>When a break is started, the operator chooses the break duration.</Bullet>
        <Bullet>Expired breaks are not ended automatically — the resource card and the End break button pulse until a dispatcher ends the break from the resource drawer.</Bullet>
        <Bullet>While a resource is Assigned, its status flashes until the crew acknowledges the tasking on their device, which moves the status to Acknowledged.</Bullet>
        <Bullet>Off Duty resources can be removed from the control room.</Bullet>
        <Bullet>A resource with nobody assigned cannot be moved from Off Duty to any other status — add at least one crew member first. The other statuses stay visible in the menu but are disabled until then.</Bullet>
      </Bullets>
    </Section>

    <Section title="Assigned staff">
      <Bullets>
        <Bullet>Staff can be added to or removed from a resource from the resource drawer.</Bullet>
        <Bullet>Assigned staff appear in the resource drawer and feed current staff-on-duty metrics.</Bullet>
        <Bullet>Removing a crew member writes a system log entry.</Bullet>
        <Bullet>If the last crew member is removed and the resource is not on an incident, the resource is automatically made Unavailable and a system log records why.</Bullet>
      </Bullets>
      <Warning>
        A person can only be crewed on one live resource at a time — across
        all active control rooms. Adding someone who is already crewed
        elsewhere is blocked with a message naming the other resource and
        room. The same guard stops an Off Duty resource being reactivated
        while its crew are live elsewhere. Remove them from the other resource
        first.
      </Warning>
    </Section>

    <Section title="Location sharing">
      <Bullets>
        <Bullet>Each crew member listed in the resource drawer shows a location-sharing status chip.</Bullet>
        <Bullet>A dispatcher can request location sharing from a crew member; the request is sent to their phone and shows as Pending until they consent.</Bullet>
        <Bullet>Approved sharing shows the time it lasts until — the end of their shift window or clock-out, whichever comes first.</Bullet>
        <Bullet>Each request writes a system log entry to the command log.</Bullet>
        <Bullet>Consents are cleared when the crew member leaves the resource, when the resource moves rooms, and when the control room closes.</Bullet>
      </Bullets>
      <Info>
        Location sharing is consent-based: the control room never sees a crew
        member's position unless they approve the request on their own device.
      </Info>
    </Section>

    <Section title="Assigning resources to incidents">
      <Bullets>
        <Bullet>Select an incident and assign a resource from the available-resource selector.</Bullet>
        <Bullet>Off Duty resources are listed in the selector but disabled — bring them on duty (which needs crew assigned) before tasking them.</Bullet>
        <Bullet>A resource can only hold one live incident at a time. Assigning a resource that is already on another incident clears it from that incident first — including incidents in a different control room — with stand-down alerts to the crew and log entries on both sides.</Bullet>
        <Bullet>If the resource is tasked in a different control room, the assignment popup shows a warning before you confirm, so crews are never silently stood down elsewhere.</Bullet>
        <Bullet>Resource assignment changes the resource state, updates the incident, and writes command log and incident log entries.</Bullet>
        <Bullet>Resource timing fields can be edited after assignment for audit accuracy.</Bullet>
      </Bullets>
    </Section>

    <Section title="Resource map position">
      <Bullets>
        <Bullet>Live map positions come from resource coordinates or linked vehicle location where available.</Bullet>
        <Bullet>On unscaled event maps, resources use manual event-map coordinates.</Bullet>
        <Bullet>Screen 2 lets an operator click or drag an unplaced resource onto the event map.</Bullet>
        <Bullet>Placed resources can be unlocked and dragged to a new event-map position.</Bullet>
        <Bullet>Manual resource position changes write a system log entry.</Bullet>
      </Bullets>
      <Screenshot url="command-control/resources-screen2.png">
        Screen 2 resource list with a selected resource, map marker, unlock control, and manual event-map position.
      </Screenshot>
    </Section>
  </Page>
)

export default Resources
